// ============================================================
// Statistiky — illustrative 90-working-day history of arrivals
// and carrier follow-up. Deterministic (seeded) so screenshots
// and tests stay stable. The pilot starts halfway: before it the
// planner calls carriers by hand, after it the 13:00 follow-up runs.
// Baselines follow the client's own numbers (10–15 no-shows a day).
// ============================================================

export const HISTORY_DAYS = 90;
export const PILOT_START_INDEX = 45;

// Small seeded PRNG (mulberry32).
export function rng(seed) {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const between = (r, min, max) => Math.round(min + r() * (max - min));

// Last HISTORY_DAYS working days (Mon–Fri) ending yesterday.
function workingDays() {
  const days = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (days.length < HISTORY_DAYS) {
    d.setDate(d.getDate() - 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) days.unshift(new Date(d));
  }
  return days;
}

function buildHistory() {
  const r = rng(20261103);
  return workingDays().map((date, i) => {
    const pilot = i >= PILOT_START_INDEX;
    // The effect ramps up over the first two pilot weeks.
    const ramp = pilot ? Math.min(1, (i - PILOT_START_INDEX + 1) / 10) : 0;
    const planned = between(r, 34, 42);
    const noShows = Math.round(between(r, 10, 15) * (1 - ramp) + between(r, 2, 5) * ramp);
    const informedPct = Math.round(between(r, 35, 50) * (1 - ramp) + between(r, 86, 96) * ramp);
    const urgencies = pilot ? between(r, 6, 11) : 0;
    const autoResolved = pilot ? Math.round(urgencies * between(r, 72, 92) / 100) : 0;
    const parkingTrucks = Math.round(between(r, 8, 14) * (1 - ramp) + between(r, 2, 5) * ramp);
    return { date: date.toISOString(), pilot, planned, noShows, informedPct, urgencies, autoResolved, parkingTrucks };
  });
}

export const history = buildHistory();

const avg = (rows, key) => (rows.length ? rows.reduce((s, r) => s + r[key], 0) / rows.length : 0);

// KPI summary: average of the last `days` days vs. the pre-pilot baseline.
export function summarize(rows, days = 30) {
  const recent = rows.slice(-days);
  const baseline = rows.filter(r => !r.pilot);
  const recentPilot = recent.filter(r => r.pilot);
  const urgencies = recentPilot.reduce((s, r) => s + r.urgencies, 0);
  const auto = recentPilot.reduce((s, r) => s + r.autoResolved, 0);
  return {
    noShows: { now: avg(recent, 'noShows'), before: avg(baseline, 'noShows') },
    informedPct: { now: avg(recent, 'informedPct'), before: avg(baseline, 'informedPct') },
    urgencies: { now: avg(recentPilot, 'urgencies'), autoPct: urgencies ? (auto / urgencies) * 100 : 0 },
    parkingTrucks: { now: avg(recent, 'parkingTrucks'), before: avg(baseline, 'parkingTrucks') },
  };
}
